/**
 * Tema do Frontend V2.
 *
 * Três estados: `system` (padrão, segue `prefers-color-scheme`), `light` e
 * `dark`. A escolha explícita marca `data-theme` no elemento raiz e, em
 * `tokens.css`, vence a preferência do sistema nos dois sentidos.
 *
 * A preferência vive em `localStorage` porque é do DISPOSITIVO, não do
 * proprietário: é o único uso legítimo de armazenamento local na V2, já que
 * nenhum dado de domínio pode morar no navegador (AGENTS §8). Navegador sem
 * armazenamento (janela privada, cookie bloqueado) cai em `system` sem erro.
 */
export type ThemeChoice = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "cosmos-v2-theme-v1";

function isChoice(value: unknown): value is ThemeChoice {
  return value === "system" || value === "light" || value === "dark";
}

export function readThemeChoice(): ThemeChoice {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isChoice(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

export function persistThemeChoice(choice: ThemeChoice): void {
  try {
    if (choice === "system") window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // Preferência visual não bloqueia o uso do produto.
  }
}

export function systemTheme(): ResolvedTheme {
  return typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";
}

export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  return choice === "system" ? systemTheme() : choice;
}

/** Aplica a escolha no elemento raiz. `system` remove o atributo. */
export function applyThemeChoice(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", choice);
}

export const THEME_LABELS: Record<ThemeChoice, string> = {
  system: "Tema do sistema",
  light: "Tema claro",
  dark: "Tema escuro",
};
